import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { 
  CircleDot, Activity, Smile, Sparkles, Scissors, Stethoscope,
  Plus, Minus, Trash2, Plane, Bed, Car, Package, FileText, 
  ChevronLeft, X, Calculator, CreditCard, Download, PanelRightOpen,
  ArrowUp, ArrowDown, MapPin, Settings, DollarSign, StickyNote, Edit3, Save
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Slider } from "./components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { ScrollArea } from "./components/ui/scroll-area";
import { Badge } from "./components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import { generatePDF } from "./utils/pdfGenerator";

const iconMap = {
  CircleDot: CircleDot,
  Activity: Activity,
  Smile: Smile,
  Sparkles: Sparkles,
  Scissors: Scissors,
  Stethoscope: Stethoscope,
};

const customItemIcons = {
  flight: Plane,
  hotel: Bed,
  transport: Car,
  other: Package,
};

const MAXILAR_OPTIONS = [
  { value: "general", label: "General", icon: null },
  { value: "superior", label: "Maxilar Superior", icon: ArrowUp },
  { value: "inferior", label: "Maxilar Inferior", icon: ArrowDown },
];

const VIAJE_OPTIONS = [1, 2, 3, 4];

// Default treatments catalog - embedded in frontend (no backend needed)
const DEFAULT_TREATMENTS_CATALOG = [
  {
    id: "protesis-implantes",
    name: "Prótesis e Implantes",
    icon: "CircleDot",
    image: "https://images.unsplash.com/photo-1650739353152-5488298a9d38?q=85&w=800&auto=format&fit=crop",
    treatments: [
      { id: "impl-titanio", name: "Implante Dental Titanio", price_usd: 850, description: "Implante de titanio de alta calidad" },
      { id: "corona-implante", name: "Corona sobre Implante", price_usd: 450, description: "Corona en porcelana sobre implante" },
      { id: "protesis-hibrida-6", name: "Prótesis Híbrida Porcelana 6 Implantes", price_usd: 8500, description: "Prótesis completa sobre 6 implantes" },
      { id: "protesis-hibrida-4", name: "Prótesis Híbrida Acrílico 4 Implantes", price_usd: 5500, description: "Prótesis completa sobre 4 implantes" },
      { id: "aditamento", name: "Aditamento para Corona", price_usd: 280, description: "Aditamento definitivo en titanio" },
      { id: "hueso-membrana", name: "Injerto de Hueso + Membrana", price_usd: 380, description: "Material de regeneración ósea" },
      { id: "protesis-trans", name: "Prótesis Transicional", price_usd: 350, description: "Prótesis temporal durante tratamiento" },
    ]
  },
  {
    id: "endodoncia",
    name: "Endodoncia",
    icon: "Activity",
    image: "https://images.unsplash.com/photo-1598256989800-fe5f95da9787?q=85&w=800&auto=format&fit=crop",
    treatments: [
      { id: "endo-uni", name: "Endodoncia Uniradicular", price_usd: 120, description: "Tratamiento conducto 1 raíz" },
      { id: "endo-bi", name: "Endodoncia Biradicular", price_usd: 180, description: "Tratamiento conducto 2 raíces" },
      { id: "endo-multi", name: "Endodoncia Multiradicular", price_usd: 250, description: "Tratamiento conducto 3+ raíces" },
    ]
  },
  {
    id: "ortodoncia",
    name: "Ortodoncia",
    icon: "Smile",
    image: "https://images.pexels.com/photos/3845985/pexels-photo-3845985.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    treatments: [
      { id: "arcos-autoligado-sup", name: "Arcos Autoligado Superior", price_usd: 1800, description: "Ortodoncia autoligado maxilar superior" },
      { id: "arcos-autoligado-inf", name: "Arcos Autoligado Inferior", price_usd: 1800, description: "Ortodoncia autoligado maxilar inferior" },
      { id: "alineadores-sup", name: "Alineadores Superior", price_usd: 2200, description: "Alineadores invisibles superiores" },
      { id: "alineadores-inf", name: "Alineadores Inferior", price_usd: 2200, description: "Alineadores invisibles inferiores" },
      { id: "retenedor", name: "Retenedor Fijo", price_usd: 150, description: "Retenedor post-ortodoncia" },
    ]
  },
  {
    id: "estetica",
    name: "Estética Dental",
    icon: "Sparkles",
    image: "https://images.pexels.com/photos/6812494/pexels-photo-6812494.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    treatments: [
      { id: "diseno-8", name: "Diseño de Sonrisa 8 Carillas", price_usd: 3200, description: "8 carillas en porcelana E-max" },
      { id: "diseno-16", name: "Diseño de Sonrisa 16 Carillas", price_usd: 5800, description: "16 carillas en porcelana E-max" },
      { id: "carilla-1", name: "Carilla Individual", price_usd: 420, description: "1 carilla en porcelana E-max" },
      { id: "blanqueamiento", name: "Blanqueamiento Dental", price_usd: 280, description: "Blanqueamiento LED profesional" },
      { id: "blanqueamiento-int", name: "Blanqueamiento Interno", price_usd: 120, description: "Blanqueamiento diente endodonciado" },
    ]
  },
  {
    id: "cirugia",
    name: "Cirugía Oral",
    icon: "Scissors",
    image: "https://images.unsplash.com/photo-1642844819197-5f5f21b89ff8?q=85&w=800&auto=format&fit=crop",
    treatments: [
      { id: "exodoncia-simple", name: "Exodoncia Simple", price_usd: 80, description: "Extracción dental simple" },
      { id: "exodoncia-compleja", name: "Exodoncia Compleja", price_usd: 180, description: "Extracción quirúrgica" },
      { id: "alargamiento", name: "Alargamiento de Corona", price_usd: 220, description: "Cirugía periodontal estética" },
    ]
  },
  {
    id: "servicios-generales",
    name: "Servicios Generales",
    icon: "Stethoscope",
    image: "https://images.unsplash.com/photo-1598256989800-fe5f95da9787?q=85&w=800&auto=format&fit=crop",
    treatments: [
      { id: "fase-higienica", name: "Fase Higiénica Completa", price_usd: 150, description: "Limpieza profunda y profilaxis" },
      { id: "valoracion", name: "Valoración Odontológica", price_usd: 50, description: "Consulta y diagnóstico" },
      { id: "radiografia-pan", name: "Radiografía Panorámica", price_usd: 40, description: "Estudio radiológico panorámico" },
      { id: "tac-dental", name: "TAC Dental", price_usd: 80, description: "Tomografía computarizada 3D" },
      { id: "sedacion-hora", name: "Sedación Consciente (por hora)", price_usd: 200, description: "Sedación IV por anestesiólogo" },
    ]
  }
];

const COUNTRIES = [
  { code: "US", name: "Estados Unidos", flag: "🇺🇸" },
  { code: "CO", name: "Colombia", flag: "🇨🇴" },
  { code: "MX", name: "México", flag: "🇲🇽" },
  { code: "ES", name: "España", flag: "🇪🇸" },
  { code: "AR", name: "Argentina", flag: "🇦🇷" },
  { code: "CL", name: "Chile", flag: "🇨🇱" },
  { code: "PE", name: "Perú", flag: "🇵🇪" },
  { code: "EC", name: "Ecuador", flag: "🇪🇨" },
  { code: "VE", name: "Venezuela", flag: "🇻🇪" },
  { code: "BR", name: "Brasil", flag: "🇧🇷" },
  { code: "CA", name: "Canadá", flag: "🇨🇦" },
  { code: "PA", name: "Panamá", flag: "🇵🇦" },
  { code: "CR", name: "Costa Rica", flag: "🇨🇷" },
  { code: "DO", name: "República Dominicana", flag: "🇩🇴" },
  { code: "PR", name: "Puerto Rico", flag: "🇵🇷" },
  { code: "OTHER", name: "Otro", flag: "🌎" },
];

function App() {
  // Use embedded catalog instead of fetching from backend
  const [categories, setCategories] = useState(DEFAULT_TREATMENTS_CATALOG);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [budgetItems, setBudgetItems] = useState([]);
  const [customItems, setCustomItems] = useState([]);
  const [currency, setCurrency] = useState("USD");
  const [exchangeRate, setExchangeRate] = useState(4200);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [financingMonths, setFinancingMonths] = useState(1);
  const [patientInfo, setPatientInfo] = useState({ name: "", phone: "", email: "", country: "" });
  const [showCustomItemModal, setShowCustomItemModal] = useState(false);
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [showFinancingModal, setShowFinancingModal] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [mobileBudgetOpen, setMobileBudgetOpen] = useState(false);
  const [loading, setLoading] = useState(false); // No loading needed - data is local
  const [customItemForm, setCustomItemForm] = useState({ name: "", price: "", type: "flight", viaje: 1 });
  const [isInitialized, setIsInitialized] = useState(false);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(true);
  
  // New state for maxilar and viaje selection
  const [selectedMaxilar, setSelectedMaxilar] = useState("general");
  const [selectedViaje, setSelectedViaje] = useState(1);
  const [showAddTreatmentModal, setShowAddTreatmentModal] = useState(false);
  const [treatmentToAdd, setTreatmentToAdd] = useState(null);
  
  // Configuration and notes state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showPricesModal, setShowPricesModal] = useState(false);
  const [budgetNotes, setBudgetNotes] = useState("");
  const [editingPrices, setEditingPrices] = useState({});
  const [tempExchangeRate, setTempExchangeRate] = useState(4200);
  
  // Custom logo state
  const [customLogo, setCustomLogo] = useState(null);
  
  // Custom treatment names and new treatments
  const [customTreatmentNames, setCustomTreatmentNames] = useState({});
  const [customTreatments, setCustomTreatments] = useState({}); // { categoryId: [treatments] }
  const [showAddTreatmentForm, setShowAddTreatmentForm] = useState(null); // categoryId or null
  const [newTreatmentForm, setNewTreatmentForm] = useState({ name: "", price: "", description: "" });

  // Load data from localStorage on mount
  useEffect(() => {
    const savedBudget = localStorage.getItem('dentalBudget');
    const savedCustom = localStorage.getItem('dentalCustomItems');
    const savedPatient = localStorage.getItem('dentalPatient');
    const savedDiscount = localStorage.getItem('dentalDiscount');
    const savedFinancing = localStorage.getItem('dentalFinancing');
    const savedNotes = localStorage.getItem('dentalNotes');
    const savedExchangeRate = localStorage.getItem('dentalExchangeRate');
    const savedPrices = localStorage.getItem('dentalCustomPrices');
    const savedLogo = localStorage.getItem('dentalCustomLogo');
    
    if (savedBudget) {
      try { setBudgetItems(JSON.parse(savedBudget)); } catch (e) { console.error(e); }
    }
    if (savedCustom) {
      try { setCustomItems(JSON.parse(savedCustom)); } catch (e) { console.error(e); }
    }
    if (savedPatient) {
      try { 
        const patient = JSON.parse(savedPatient);
        setPatientInfo(patient);
        // If patient already has name, skip welcome screen
        if (patient.name) {
          setShowWelcomeScreen(false);
        }
      } catch (e) { console.error(e); }
    }
    if (savedDiscount) setDiscountPercent(parseFloat(savedDiscount) || 0);
    if (savedFinancing) setFinancingMonths(parseInt(savedFinancing) || 1);
    if (savedNotes) setBudgetNotes(savedNotes);
    if (savedExchangeRate) {
      const rate = parseFloat(savedExchangeRate);
      setExchangeRate(rate);
      setTempExchangeRate(rate);
    }
    if (savedPrices) {
      try { setEditingPrices(JSON.parse(savedPrices)); } catch (e) { console.error(e); }
    }
    if (savedLogo) {
      setCustomLogo(savedLogo);
    }
    
    // Load custom treatment names
    const savedNames = localStorage.getItem('dentalCustomNames');
    if (savedNames) {
      try { setCustomTreatmentNames(JSON.parse(savedNames)); } catch (e) { console.error(e); }
    }
    
    // Load custom added treatments
    const savedCustomTreatments = localStorage.getItem('dentalCustomTreatments');
    if (savedCustomTreatments) {
      try { setCustomTreatments(JSON.parse(savedCustomTreatments)); } catch (e) { console.error(e); }
    }
    
    // Mark as initialized after loading
    setIsInitialized(true);
  }, []);

  // Save to localStorage when data changes (only after initialization)
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('dentalBudget', JSON.stringify(budgetItems));
    }
  }, [budgetItems, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('dentalCustomItems', JSON.stringify(customItems));
    }
  }, [customItems, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('dentalPatient', JSON.stringify(patientInfo));
    }
  }, [patientInfo, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('dentalDiscount', discountPercent.toString());
    }
  }, [discountPercent, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('dentalFinancing', financingMonths.toString());
    }
  }, [financingMonths, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('dentalNotes', budgetNotes);
    }
  }, [budgetNotes, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('dentalExchangeRate', exchangeRate.toString());
    }
  }, [exchangeRate, isInitialized]);

  useEffect(() => {
    if (isInitialized && Object.keys(editingPrices).length > 0) {
      localStorage.setItem('dentalCustomPrices', JSON.stringify(editingPrices));
    }
  }, [editingPrices, isInitialized]);

  useEffect(() => {
    if (isInitialized && customLogo) {
      localStorage.setItem('dentalCustomLogo', customLogo);
    }
  }, [customLogo, isInitialized]);

  useEffect(() => {
    if (isInitialized && Object.keys(customTreatmentNames).length > 0) {
      localStorage.setItem('dentalCustomNames', JSON.stringify(customTreatmentNames));
    }
  }, [customTreatmentNames, isInitialized]);

  useEffect(() => {
    if (isInitialized && Object.keys(customTreatments).length > 0) {
      localStorage.setItem('dentalCustomTreatments', JSON.stringify(customTreatments));
    }
  }, [customTreatments, isInitialized]);

  // Handle logo upload
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("El logo debe ser menor a 2MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setCustomLogo(reader.result);
        toast.success("Logo actualizado");
      };
      reader.readAsDataURL(file);
    }
  };

  // No backend fetch needed - using embedded catalog
  // Categories are loaded from DEFAULT_TREATMENTS_CATALOG

  // Calculate totals
  const calculateTotals = useCallback(() => {
    const treatmentSubtotal = budgetItems.reduce((sum, item) => sum + (item.price_usd * item.quantity), 0);
    const customSubtotal = customItems.reduce((sum, item) => sum + item.price_usd, 0);
    const subtotal = treatmentSubtotal + customSubtotal;
    const discountAmount = subtotal * (discountPercent / 100);
    const total = subtotal - discountAmount;
    
    let monthlyPayment = total;
    if (financingMonths > 1) {
      const interestRate = 0.015;
      monthlyPayment = (total * (1 + interestRate * financingMonths)) / financingMonths;
    }

    return {
      subtotal,
      discountAmount,
      total,
      monthlyPayment,
      treatmentCount: budgetItems.reduce((sum, item) => sum + item.quantity, 0),
      customCount: customItems.length
    };
  }, [budgetItems, customItems, discountPercent, financingMonths]);

  const totals = calculateTotals();

  // Get custom price for a treatment (if edited)
  const getPrice = (treatment) => {
    return editingPrices[treatment.id] !== undefined 
      ? editingPrices[treatment.id] 
      : treatment.price_usd;
  };

  // Get custom name for a treatment (if edited)
  const getName = (treatment) => {
    return customTreatmentNames[treatment.id] !== undefined 
      ? customTreatmentNames[treatment.id] 
      : treatment.name;
  };

  // Update name for a treatment
  const updateTreatmentName = (treatmentId, newName) => {
    setCustomTreatmentNames(prev => ({
      ...prev,
      [treatmentId]: newName
    }));
  };

  // Update price for a treatment
  const updateTreatmentPrice = (treatmentId, newPrice) => {
    setEditingPrices(prev => ({
      ...prev,
      [treatmentId]: parseFloat(newPrice) || 0
    }));
  };

  // Add new treatment to a category
  const addNewTreatment = (categoryId) => {
    if (!newTreatmentForm.name || !newTreatmentForm.price) {
      toast.error("Nombre y precio son obligatorios");
      return;
    }
    
    const newTreatment = {
      id: `custom-${categoryId}-${Date.now()}`,
      name: newTreatmentForm.name,
      price_usd: parseFloat(newTreatmentForm.price) || 0,
      description: newTreatmentForm.description || "Tratamiento personalizado",
      isCustom: true
    };
    
    setCustomTreatments(prev => ({
      ...prev,
      [categoryId]: [...(prev[categoryId] || []), newTreatment]
    }));
    
    setNewTreatmentForm({ name: "", price: "", description: "" });
    setShowAddTreatmentForm(null);
    toast.success("Tratamiento agregado");
  };

  // Delete custom treatment
  const deleteCustomTreatment = (categoryId, treatmentId) => {
    setCustomTreatments(prev => ({
      ...prev,
      [categoryId]: (prev[categoryId] || []).filter(t => t.id !== treatmentId)
    }));
    toast.info("Tratamiento eliminado");
  };

  // Get all treatments for a category (default + custom)
  const getCategoryTreatments = (category) => {
    const defaultTreatments = category.treatments || [];
    const customCategoryTreatments = customTreatments[category.id] || [];
    return [...defaultTreatments, ...customCategoryTreatments];
  };

  // Save exchange rate
  const saveExchangeRate = () => {
    setExchangeRate(tempExchangeRate);
    toast.success(`TRM actualizada: $${tempExchangeRate.toLocaleString('es-CO')} COP/USD`);
    setShowConfigModal(false);
  };

  // Format currency
  const formatPrice = (usd) => {
    if (currency === "USD") {
      return `$${usd.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} USD`;
    }
    const cop = usd * exchangeRate;
    return `$${cop.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} COP`;
  };

  // Open add treatment modal with maxilar/viaje selection
  const openAddTreatmentModal = (treatment, category) => {
    setTreatmentToAdd({ treatment, category });
    setShowAddTreatmentModal(true);
  };

  // Add treatment to budget with maxilar and viaje
  const addToBudget = () => {
    if (!treatmentToAdd) return;
    
    const { treatment, category } = treatmentToAdd;
    const itemKey = `${treatment.id}-${selectedMaxilar}-${selectedViaje}`;
    const price = getPrice(treatment);
    const displayName = getName(treatment);
    
    const existingIndex = budgetItems.findIndex(item => 
      item.treatment_id === treatment.id && 
      item.maxilar === selectedMaxilar && 
      item.viaje === selectedViaje
    );
    
    if (existingIndex >= 0) {
      const updated = [...budgetItems];
      updated[existingIndex].quantity += 1;
      setBudgetItems(updated);
    } else {
      setBudgetItems([...budgetItems, {
        treatment_id: treatment.id,
        treatment_name: displayName,
        category: category.name,
        quantity: 1,
        price_usd: price,
        maxilar: selectedMaxilar,
        viaje: selectedViaje,
        itemKey: itemKey,
        comment: ""
      }]);
    }
    
    const maxilarLabel = MAXILAR_OPTIONS.find(m => m.value === selectedMaxilar)?.label || "";
    toast.success(`${displayName} agregado - ${maxilarLabel} - Viaje ${selectedViaje}`);
    setShowAddTreatmentModal(false);
    setTreatmentToAdd(null);
  };

  // Quick add without modal (for general items)
  const quickAddToBudget = (treatment, category) => {
    const itemKey = `${treatment.id}-general-1`;
    const price = getPrice(treatment);
    const displayName = getName(treatment);
    
    const existingIndex = budgetItems.findIndex(item => 
      item.treatment_id === treatment.id && 
      item.maxilar === "general" && 
      item.viaje === 1
    );
    
    if (existingIndex >= 0) {
      const updated = [...budgetItems];
      updated[existingIndex].quantity += 1;
      setBudgetItems(updated);
    } else {
      setBudgetItems([...budgetItems, {
        treatment_id: treatment.id,
        treatment_name: displayName,
        category: category.name,
        quantity: 1,
        price_usd: price,
        maxilar: "general",
        viaje: 1,
        itemKey: itemKey,
        comment: ""
      }]);
    }
    toast.success(`${displayName} agregado`);
  };

  // Update item comment
  const updateItemComment = (itemKey, comment) => {
    setBudgetItems(budgetItems.map(item => 
      item.itemKey === itemKey ? { ...item, comment } : item
    ));
  };

  // Update quantity
  const updateQuantity = (itemKey, delta) => {
    const updated = budgetItems.map(item => {
      if (item.itemKey === itemKey) {
        const newQty = Math.max(0, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(item => item.quantity > 0);
    setBudgetItems(updated);
  };

  // Remove budget item
  const removeBudgetItem = (itemKey) => {
    setBudgetItems(budgetItems.filter(item => item.itemKey !== itemKey));
    toast.info("Tratamiento eliminado");
  };

  // Group budget items by viaje
  const groupedByViaje = budgetItems.reduce((acc, item) => {
    const viaje = item.viaje || 1;
    if (!acc[viaje]) acc[viaje] = [];
    acc[viaje].push(item);
    return acc;
  }, {});

  // Group custom items by viaje
  const customGroupedByViaje = customItems.reduce((acc, item) => {
    const viaje = item.viaje || 1;
    if (!acc[viaje]) acc[viaje] = [];
    acc[viaje].push(item);
    return acc;
  }, {});

  // Get all unique viajes
  const allViajes = [...new Set([
    ...Object.keys(groupedByViaje).map(Number),
    ...Object.keys(customGroupedByViaje).map(Number)
  ])].sort((a, b) => a - b);

  // Calculate totals per viaje
  const calculateViajeTotal = (viaje) => {
    const treatmentTotal = (groupedByViaje[viaje] || []).reduce((sum, item) => sum + (item.price_usd * item.quantity), 0);
    const customTotal = (customGroupedByViaje[viaje] || []).reduce((sum, item) => sum + item.price_usd, 0);
    return treatmentTotal + customTotal;
  };

  // Add custom item
  const addCustomItem = () => {
    if (!customItemForm.name || !customItemForm.price) {
      toast.error("Completa todos los campos");
      return;
    }
    
    const newItem = {
      id: `custom-${Date.now()}`,
      name: customItemForm.name,
      price_usd: parseFloat(customItemForm.price),
      item_type: customItemForm.type,
      viaje: customItemForm.viaje || 1
    };
    
    setCustomItems([...customItems, newItem]);
    setCustomItemForm({ name: "", price: "", type: "flight", viaje: 1 });
    setShowCustomItemModal(false);
    toast.success(`Item agregado - Viaje ${newItem.viaje}`);
  };

  // Remove custom item
  const removeCustomItem = (itemId) => {
    setCustomItems(customItems.filter(item => item.id !== itemId));
    toast.info("Item eliminado");
  };

  // Clear budget
  const clearBudget = () => {
    setBudgetItems([]);
    setCustomItems([]);
    setDiscountPercent(0);
    setFinancingMonths(1);
    localStorage.removeItem('dentalBudget');
    localStorage.removeItem('dentalCustomItems');
    localStorage.removeItem('dentalDiscount');
    localStorage.removeItem('dentalFinancing');
    toast.info("Presupuesto limpiado");
  };

  // Generate PDF
  const handleGeneratePDF = async () => {
    if (budgetItems.length === 0 && customItems.length === 0) {
      toast.error("Agrega items al presupuesto");
      return;
    }
    
    if (!patientInfo.name) {
      setShowPatientModal(true);
      toast.info("Ingresa los datos del paciente");
      return;
    }

    try {
      await generatePDF({
        patient: patientInfo,
        items: budgetItems,
        customItems,
        totals,
        discountPercent,
        financingMonths,
        exchangeRate,
        currency,
        notes: budgetNotes,
        customLogo: customLogo
      });
      toast.success("PDF generado exitosamente");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Error al generar PDF");
    }
  };

  const IconComponent = ({ iconName, className }) => {
    const Icon = iconMap[iconName] || CircleDot;
    return <Icon className={className} />;
  };

  // Start session with patient info
  const startSession = () => {
    if (!patientInfo.name.trim()) {
      toast.error("El nombre del paciente es obligatorio");
      return;
    }
    setShowWelcomeScreen(false);
    toast.success(`¡Bienvenido/a ${patientInfo.name}!`);
  };

  // New session - clear all and show welcome
  const newSession = () => {
    setBudgetItems([]);
    setCustomItems([]);
    setDiscountPercent(0);
    setFinancingMonths(1);
    setBudgetNotes("");
    setPatientInfo({ name: "", phone: "", email: "", country: "" });
    localStorage.removeItem('dentalBudget');
    localStorage.removeItem('dentalCustomItems');
    localStorage.removeItem('dentalPatient');
    localStorage.removeItem('dentalDiscount');
    localStorage.removeItem('dentalFinancing');
    localStorage.removeItem('dentalNotes');
    setShowWelcomeScreen(true);
    toast.info("Nueva sesión iniciada");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-[#064E3B] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-stone-600 font-medium">Cargando catálogo...</p>
        </motion.div>
      </div>
    );
  }

  // Welcome Screen - Patient Info Form
  if (showWelcomeScreen) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-4" data-testid="welcome-screen">
        <Toaster position="top-right" richColors />
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-3xl shadow-xl p-8 border border-stone-200">
            {/* Logo Upload Section */}
            <div className="flex flex-col items-center mb-8">
              <label 
                htmlFor="logo-upload" 
                className="cursor-pointer group"
              >
                {customLogo ? (
                  <div className="relative">
                    <img 
                      src={customLogo} 
                      alt="Tu Logo"
                      className="h-16 max-w-[200px] object-contain"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <span className="text-white text-xs font-medium">Cambiar logo</span>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-stone-300 rounded-xl px-8 py-4 hover:border-[#0070F3] transition-colors">
                    <p className="text-stone-400 font-medium text-center">YOUR LOGO</p>
                    <p className="text-stone-400 text-xs text-center mt-1">Click para subir</p>
                  </div>
                )}
              </label>
              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-upload-input"
              />
              {customLogo && (
                <button 
                  onClick={() => {
                    setCustomLogo(null);
                    localStorage.removeItem('dentalCustomLogo');
                  }}
                  className="text-xs text-stone-400 hover:text-red-500 mt-2"
                >
                  Eliminar logo
                </button>
              )}
            </div>

            <h1 className="text-2xl font-bold text-stone-900 text-center mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Nueva Valoración
            </h1>
            <p className="text-stone-500 text-center mb-8">
              Ingresa los datos del paciente para comenzar
            </p>

            <div className="space-y-5">
              {/* Name */}
              <div>
                <Label htmlFor="welcome-name" className="text-sm font-semibold text-stone-700">
                  Nombre del Paciente *
                </Label>
                <Input
                  id="welcome-name"
                  placeholder="Nombre completo"
                  value={patientInfo.name}
                  onChange={(e) => setPatientInfo({ ...patientInfo, name: e.target.value })}
                  className="mt-1.5 h-12 rounded-xl border-stone-200 focus:border-[#0070F3] focus:ring-[#0070F3]"
                  data-testid="welcome-name-input"
                />
              </div>

              {/* Country */}
              <div>
                <Label className="text-sm font-semibold text-stone-700">
                  País de Origen *
                </Label>
                <Select 
                  value={patientInfo.country} 
                  onValueChange={(value) => setPatientInfo({ ...patientInfo, country: value })}
                >
                  <SelectTrigger className="mt-1.5 h-12 rounded-xl border-stone-200" data-testid="welcome-country-select">
                    <SelectValue placeholder="Selecciona un país" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        <span className="flex items-center gap-2">
                          <span>{country.flag}</span>
                          <span>{country.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="welcome-phone" className="text-sm font-semibold text-stone-700">
                  Teléfono / WhatsApp
                </Label>
                <Input
                  id="welcome-phone"
                  placeholder="+1 234 567 8900"
                  value={patientInfo.phone}
                  onChange={(e) => setPatientInfo({ ...patientInfo, phone: e.target.value })}
                  className="mt-1.5 h-12 rounded-xl border-stone-200"
                  data-testid="welcome-phone-input"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="welcome-email" className="text-sm font-semibold text-stone-700">
                  Correo Electrónico
                </Label>
                <Input
                  id="welcome-email"
                  type="email"
                  placeholder="paciente@email.com"
                  value={patientInfo.email}
                  onChange={(e) => setPatientInfo({ ...patientInfo, email: e.target.value })}
                  className="mt-1.5 h-12 rounded-xl border-stone-200"
                  data-testid="welcome-email-input"
                />
              </div>

              {/* Start Button */}
              <Button
                onClick={startSession}
                disabled={!patientInfo.name.trim()}
                className="w-full h-14 bg-[#0070F3] hover:bg-[#0060D3] text-white rounded-full font-semibold text-lg mt-4 transition-all"
                data-testid="start-session-btn"
              >
                Comenzar Presupuesto
              </Button>
            </div>
          </div>

          <p className="text-center text-stone-400 text-sm mt-6">
            Calculador de Presupuestos Dentales
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="app-container noise-bg" data-testid="app-container">
      <Toaster position="top-right" richColors />
      
      <div className="split-layout">
        {/* Left Panel - Catalog */}
        <div className="catalog-panel">
          {/* Header */}
          <motion.header 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between mb-6"
          >
            <div className="flex items-center gap-4">
              {customLogo ? (
                <img 
                  src={customLogo} 
                  alt="Logo"
                  className="h-10 md:h-12 max-w-[180px] object-contain"
                  data-testid="company-logo"
                />
              ) : (
                <div className="text-stone-400 font-medium text-lg">YOUR LOGO</div>
              )}
            </div>
            
            {/* Currency Toggle and Settings */}
            <div className="flex items-center gap-2">
              <div className="currency-toggle" data-testid="currency-toggle">
                <button 
                  className={`currency-toggle-btn ${currency === 'USD' ? 'active' : ''}`}
                  onClick={() => setCurrency('USD')}
                  data-testid="currency-usd-btn"
                >
                  USD
                </button>
                <button 
                  className={`currency-toggle-btn ${currency === 'COP' ? 'active' : ''}`}
                  onClick={() => setCurrency('COP')}
                  data-testid="currency-cop-btn"
                >
                  COP
                </button>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  setTempExchangeRate(exchangeRate);
                  setShowConfigModal(true);
                }}
                className="rounded-full h-10 w-10"
                data-testid="config-btn"
                title="Configuración"
              >
                <Settings className="w-4 h-4" />
              </Button>
            </div>
          </motion.header>

          {/* Patient Info Bar */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white border border-stone-200 rounded-2xl p-4 mb-6 flex items-center justify-between"
            data-testid="patient-info-bar"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0070F3]/10 rounded-full flex items-center justify-center">
                <span className="text-lg">
                  {COUNTRIES.find(c => c.code === patientInfo.country)?.flag || "👤"}
                </span>
              </div>
              <div>
                <p className="font-semibold text-stone-900">{patientInfo.name}</p>
                <p className="text-xs text-stone-500">
                  {COUNTRIES.find(c => c.code === patientInfo.country)?.name || ""}
                  {patientInfo.phone && ` • ${patientInfo.phone}`}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={newSession}
              className="rounded-full text-xs"
              data-testid="new-session-btn"
            >
              Nueva Sesión
            </Button>
          </motion.div>

          {/* Category Selection or Treatment List */}
          <AnimatePresence mode="wait">
            {!selectedCategory ? (
              <motion.div
                key="categories"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <h2 className="text-xl font-bold text-stone-800 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Categorías de Tratamiento
                </h2>
                <div className="category-grid" data-testid="category-grid">
                  {categories.map((category, index) => (
                    <motion.div
                      key={category.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white border border-stone-200 rounded-2xl p-6 cursor-pointer hover:border-[#064E3B] hover:shadow-lg transition-all"
                      onClick={() => setSelectedCategory(category)}
                      data-testid={`category-card-${category.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-[#064E3B]/10 flex items-center justify-center">
                          <IconComponent iconName={category.icon} className="w-7 h-7 text-[#064E3B]" />
                        </div>
                        <div>
                          <h3 className="font-bold text-stone-900">{category.name}</h3>
                          <p className="text-stone-500 text-sm">
                            {getCategoryTreatments(category).length} tratamientos
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="treatments"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="flex items-center gap-2 text-stone-600 hover:text-[#064E3B] transition-colors mb-6 font-medium"
                  data-testid="back-to-categories-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                  Volver a categorías
                </button>
                
                <div className="flex items-center gap-3 mb-6">
                  <IconComponent iconName={selectedCategory.icon} className="w-8 h-8 text-[#064E3B]" />
                  <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {selectedCategory.name}
                  </h2>
                </div>

                <div className="treatment-list" data-testid="treatment-list">
                  {getCategoryTreatments(selectedCategory).map((treatment, index) => {
                    const price = getPrice(treatment);
                    const displayName = getName(treatment);
                    const isEdited = editingPrices[treatment.id] !== undefined && editingPrices[treatment.id] !== treatment.price_usd;
                    const isNameEdited = customTreatmentNames[treatment.id] !== undefined;
                    
                    return (
                      <motion.div
                        key={treatment.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className={`treatment-item ${isEdited || isNameEdited ? 'border-[#0070F3]/30' : ''}`}
                        data-testid={`treatment-item-${treatment.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-stone-900 text-sm md:text-base">
                            {displayName}
                            {treatment.isCustom && (
                              <Badge variant="outline" className="ml-2 text-xs py-0">Personalizado</Badge>
                            )}
                          </h3>
                          <p className="text-stone-500 text-xs md:text-sm mt-0.5 truncate">
                            {treatment.description}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="text-right">
                            <span className="price-tag text-[#064E3B] font-semibold text-sm md:text-base whitespace-nowrap">
                              {formatPrice(price)}
                            </span>
                            {isEdited && (
                              <p className="text-xs text-stone-400 line-through">
                                {formatPrice(treatment.price_usd)}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => quickAddToBudget(treatment, selectedCategory)}
                            className="rounded-full px-3 h-9 text-xs"
                            data-testid={`quick-add-btn-${treatment.id}`}
                            title="Agregar rápido (General)"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openAddTreatmentModal(treatment, selectedCategory)}
                            className="bg-[#064E3B] hover:bg-[#065f46] text-white rounded-full px-3 md:px-4 h-9"
                            data-testid={`add-treatment-btn-${treatment.id}`}
                            title="Agregar con maxilar y viaje"
                          >
                            <MapPin className="w-4 h-4" />
                          </Button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right Panel - Budget Sidebar */}
        <motion.div 
          className={`budget-panel ${mobileBudgetOpen ? 'open' : ''}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          data-testid="budget-panel"
        >
          {/* Mobile Handle */}
          <div 
            className="lg:hidden flex justify-center py-2 cursor-pointer"
            onClick={() => setMobileBudgetOpen(!mobileBudgetOpen)}
          >
            <div className="w-12 h-1 bg-stone-300 rounded-full" />
          </div>

          {/* Budget Header */}
          <div className="p-4 md:p-6 border-b border-stone-200">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Presupuesto
              </h2>
              {(budgetItems.length > 0 || customItems.length > 0) && (
                <button
                  onClick={clearBudget}
                  className="text-stone-400 hover:text-red-500 transition-colors text-sm"
                  data-testid="clear-budget-btn"
                >
                  Limpiar
                </button>
              )}
            </div>
            <p className="text-stone-500 text-sm">
              {totals.treatmentCount + totals.customCount} items
            </p>
          </div>

          {/* Budget Items */}
          <ScrollArea className="flex-1 p-4 md:p-6">
            {budgetItems.length === 0 && customItems.length === 0 ? (
              <div className="empty-state">
                <FileText className="w-12 h-12 text-stone-300 mb-3" />
                <p className="text-stone-500 font-medium">Sin items</p>
                <p className="text-stone-400 text-sm mt-1">Selecciona tratamientos del catálogo</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="budget-items-list">
                {/* Group by Viaje */}
                {allViajes.map((viaje) => (
                  <div key={viaje} className="viaje-group">
                    {/* Viaje Header */}
                    <div className="flex items-center justify-between mb-3 pb-2 border-b-2 border-[#064E3B]">
                      <div className="flex items-center gap-2">
                        <Plane className="w-4 h-4 text-[#064E3B]" />
                        <span className="font-bold text-[#064E3B] text-sm uppercase tracking-wide">
                          Viaje {viaje}
                        </span>
                      </div>
                      <span className="price-tag text-[#064E3B] font-bold text-sm">
                        {formatPrice(calculateViajeTotal(viaje))}
                      </span>
                    </div>

                    {/* Treatment Items for this Viaje */}
                    {(groupedByViaje[viaje] || []).map((item) => {
                      const maxilarInfo = MAXILAR_OPTIONS.find(m => m.value === item.maxilar);
                      const MaxilarIcon = maxilarInfo?.icon;
                      return (
                        <motion.div
                          key={item.itemKey}
                          layout
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          className="budget-item flex-col gap-2"
                          data-testid={`budget-item-${item.itemKey}`}
                        >
                          <div className="flex justify-between items-start w-full">
                            <div className="flex-1 min-w-0 pr-2">
                              <p className="font-medium text-stone-900 text-sm truncate">{item.treatment_name}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-stone-500">{item.category}</span>
                                {item.maxilar !== "general" && (
                                  <Badge variant="outline" className="text-xs py-0 h-5 gap-1">
                                    {MaxilarIcon && <MaxilarIcon className="w-3 h-3" />}
                                    {maxilarInfo?.label?.replace("Maxilar ", "")}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => removeBudgetItem(item.itemKey)}
                              className="text-stone-400 hover:text-red-500 transition-colors p-1"
                              data-testid={`remove-item-btn-${item.itemKey}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex justify-between items-center w-full">
                            <div className="quantity-control">
                              <button 
                                className="quantity-btn"
                                onClick={() => updateQuantity(item.itemKey, -1)}
                                data-testid={`decrease-qty-btn-${item.itemKey}`}
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="quantity-value">{item.quantity}</span>
                              <button 
                                className="quantity-btn"
                                onClick={() => updateQuantity(item.itemKey, 1)}
                                data-testid={`increase-qty-btn-${item.itemKey}`}
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            <span className="price-tag text-stone-900 font-semibold text-sm">
                              {formatPrice(item.price_usd * item.quantity)}
                            </span>
                          </div>
                          {/* Comment field for item */}
                          <input
                            type="text"
                            placeholder="Agregar comentario..."
                            value={item.comment || ""}
                            onChange={(e) => updateItemComment(item.itemKey, e.target.value)}
                            className="w-full text-xs px-3 py-2 border border-stone-200 rounded-lg bg-stone-50 focus:bg-white focus:border-[#0070F3] focus:outline-none transition-colors"
                            data-testid={`comment-input-${item.itemKey}`}
                          />
                        </motion.div>
                      );
                    })}

                    {/* Custom Items for this Viaje */}
                    {(customGroupedByViaje[viaje] || []).map((item) => {
                      const CustomIcon = customItemIcons[item.item_type] || Package;
                      return (
                        <motion.div
                          key={item.id}
                          layout
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="flex items-center justify-between py-2 pl-2 border-l-2 border-stone-200 ml-2"
                          data-testid={`custom-item-${item.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <CustomIcon className="w-4 h-4 text-stone-400" />
                            <span className="text-sm text-stone-700">{item.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="price-tag text-stone-900 text-sm">
                              {formatPrice(item.price_usd)}
                            </span>
                            <button
                              onClick={() => removeCustomItem(item.id)}
                              className="text-stone-400 hover:text-red-500 transition-colors"
                              data-testid={`remove-custom-btn-${item.id}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Budget Summary */}
          <div className="p-4 md:p-6 border-t border-stone-200 bg-stone-50/50">
            {/* Notes Field */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <StickyNote className="w-4 h-4 text-stone-500" />
                <Label className="text-sm text-stone-600">Notas del Presupuesto</Label>
              </div>
              <Textarea
                placeholder="Agregar notas, observaciones o condiciones especiales..."
                value={budgetNotes}
                onChange={(e) => setBudgetNotes(e.target.value)}
                className="min-h-[60px] text-sm rounded-xl resize-none border-stone-200"
                data-testid="budget-notes-textarea"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCustomItemModal(true)}
                className="flex-1 rounded-full text-xs"
                data-testid="add-custom-item-btn"
              >
                <Plus className="w-3 h-3 mr-1" />
                Item
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFinancingModal(true)}
                className="flex-1 rounded-full text-xs"
                data-testid="financing-btn"
              >
                <CreditCard className="w-3 h-3 mr-1" />
                Cuotas
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowComparisonModal(true)}
                className="flex-1 rounded-full text-xs"
                data-testid="comparison-btn"
              >
                <Calculator className="w-3 h-3 mr-1" />
                Comparar
              </Button>
            </div>

            {/* Discount Slider */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-stone-600">Descuento</span>
                {discountPercent > 0 && (
                  <Badge className="bg-[#C2410C] text-white text-xs">
                    -{discountPercent}%
                  </Badge>
                )}
              </div>
              <Slider
                value={[discountPercent]}
                onValueChange={(value) => setDiscountPercent(value[0])}
                max={30}
                step={5}
                className="w-full"
                data-testid="discount-slider"
              />
            </div>

            {/* Totals */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-stone-600">
                <span>Subtotal</span>
                <span className="price-tag">{formatPrice(totals.subtotal)}</span>
              </div>
              {discountPercent > 0 && (
                <div className="flex justify-between text-[#C2410C]">
                  <span>Descuento ({discountPercent}%)</span>
                  <span className="price-tag">-{formatPrice(totals.discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between text-stone-900 font-bold text-lg pt-2 border-t border-stone-200">
                <span>Total</span>
                <span className="price-tag" data-testid="total-price">{formatPrice(totals.total)}</span>
              </div>
              {financingMonths > 1 && (
                <div className="flex justify-between text-[#064E3B] text-sm">
                  <span>{financingMonths} cuotas de</span>
                  <span className="price-tag font-semibold">{formatPrice(totals.monthlyPayment)}</span>
                </div>
              )}
            </div>

            {/* Generate PDF Button */}
            <Button
              onClick={handleGeneratePDF}
              className="w-full mt-4 bg-[#064E3B] hover:bg-[#065f46] text-white rounded-full h-12 font-semibold"
              disabled={budgetItems.length === 0 && customItems.length === 0}
              data-testid="generate-pdf-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              Generar PDF
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Mobile Budget Toggle */}
      <motion.button
        className="mobile-budget-toggle bg-[#064E3B] text-white p-4 rounded-full shadow-lg"
        onClick={() => setMobileBudgetOpen(!mobileBudgetOpen)}
        whileTap={{ scale: 0.95 }}
        data-testid="mobile-budget-toggle"
      >
        <PanelRightOpen className="w-6 h-6" />
        {(budgetItems.length > 0 || customItems.length > 0) && (
          <span className="absolute -top-1 -right-1 bg-[#C2410C] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
            {totals.treatmentCount + totals.customCount}
          </span>
        )}
      </motion.button>

      {/* Custom Item Modal */}
      <Dialog open={showCustomItemModal} onOpenChange={setShowCustomItemModal}>
        <DialogContent className="rounded-2xl" data-testid="custom-item-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Agregar Item Personalizado</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Tipo de Item</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {[
                  { type: 'flight', label: 'Vuelo', icon: Plane },
                  { type: 'hotel', label: 'Hotel', icon: Bed },
                  { type: 'transport', label: 'Transporte', icon: Car },
                  { type: 'other', label: 'Otro', icon: Package },
                ].map(({ type, label, icon: Icon }) => (
                  <button
                    key={type}
                    onClick={() => setCustomItemForm({ ...customItemForm, type })}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-all ${
                      customItemForm.type === type 
                        ? 'border-[#064E3B] bg-[#064E3B]/5' 
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    data-testid={`custom-type-${type}`}
                  >
                    <Icon className={`w-5 h-5 ${customItemForm.type === type ? 'text-[#064E3B]' : 'text-stone-500'}`} />
                    <span className={`text-xs ${customItemForm.type === type ? 'text-[#064E3B] font-medium' : 'text-stone-500'}`}>
                      {label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="custom-name">Descripción</Label>
              <Input
                id="custom-name"
                placeholder="Ej: Boleto aéreo Miami-Bogotá"
                value={customItemForm.name}
                onChange={(e) => setCustomItemForm({ ...customItemForm, name: e.target.value })}
                className="mt-1 rounded-lg"
                data-testid="custom-item-name-input"
              />
            </div>
            <div>
              <Label htmlFor="custom-price">Precio (USD)</Label>
              <Input
                id="custom-price"
                type="number"
                placeholder="0"
                value={customItemForm.price}
                onChange={(e) => setCustomItemForm({ ...customItemForm, price: e.target.value })}
                className="mt-1 rounded-lg font-mono"
                data-testid="custom-item-price-input"
              />
            </div>
            <div>
              <Label>Asignar a Viaje</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {VIAJE_OPTIONS.map((viaje) => (
                  <button
                    key={viaje}
                    onClick={() => setCustomItemForm({ ...customItemForm, viaje })}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-all ${
                      customItemForm.viaje === viaje 
                        ? 'border-[#064E3B] bg-[#064E3B]/5' 
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    data-testid={`custom-viaje-${viaje}`}
                  >
                    <Plane className={`w-4 h-4 ${customItemForm.viaje === viaje ? 'text-[#064E3B]' : 'text-stone-500'}`} />
                    <span className={`text-xs ${customItemForm.viaje === viaje ? 'text-[#064E3B] font-medium' : 'text-stone-500'}`}>
                      Viaje {viaje}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <Button
              onClick={addCustomItem}
              className="w-full bg-[#064E3B] hover:bg-[#065f46] rounded-full"
              data-testid="confirm-add-custom-btn"
            >
              Agregar Item
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Patient Info Modal */}
      <Dialog open={showPatientModal} onOpenChange={setShowPatientModal}>
        <DialogContent className="rounded-2xl" data-testid="patient-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Datos del Paciente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="patient-name">Nombre Completo *</Label>
              <Input
                id="patient-name"
                placeholder="Juan Pérez"
                value={patientInfo.name}
                onChange={(e) => setPatientInfo({ ...patientInfo, name: e.target.value })}
                className="mt-1 rounded-lg"
                data-testid="patient-name-input"
              />
            </div>
            <div>
              <Label htmlFor="patient-phone">Teléfono</Label>
              <Input
                id="patient-phone"
                placeholder="+1 234 567 8900"
                value={patientInfo.phone}
                onChange={(e) => setPatientInfo({ ...patientInfo, phone: e.target.value })}
                className="mt-1 rounded-lg"
                data-testid="patient-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="patient-email">Email</Label>
              <Input
                id="patient-email"
                type="email"
                placeholder="juan@email.com"
                value={patientInfo.email}
                onChange={(e) => setPatientInfo({ ...patientInfo, email: e.target.value })}
                className="mt-1 rounded-lg"
                data-testid="patient-email-input"
              />
            </div>
            <Button
              onClick={() => {
                setShowPatientModal(false);
                handleGeneratePDF();
              }}
              className="w-full bg-[#064E3B] hover:bg-[#065f46] rounded-full"
              disabled={!patientInfo.name}
              data-testid="confirm-patient-btn"
            >
              Continuar y Generar PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Financing Modal */}
      <Dialog open={showFinancingModal} onOpenChange={setShowFinancingModal}>
        <DialogContent className="rounded-2xl" data-testid="financing-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Simulador de Financiamiento</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <div>
              <div className="flex justify-between items-center mb-3">
                <Label>Número de Cuotas</Label>
                <span className="text-2xl font-bold text-[#064E3B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {financingMonths}
                </span>
              </div>
              <Slider
                value={[financingMonths]}
                onValueChange={(value) => setFinancingMonths(value[0])}
                min={1}
                max={24}
                step={1}
                className="w-full"
                data-testid="financing-slider"
              />
              <div className="flex justify-between text-xs text-stone-500 mt-2">
                <span>1 cuota</span>
                <span>24 cuotas</span>
              </div>
            </div>

            <div className="financing-card">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-stone-500 mb-1">Total a financiar</p>
                  <p className="font-bold text-stone-900 price-tag">{formatPrice(totals.total)}</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 mb-1">Cuota mensual</p>
                  <p className="font-bold text-[#064E3B] price-tag text-lg">{formatPrice(totals.monthlyPayment)}</p>
                </div>
              </div>
              {financingMonths > 1 && (
                <p className="text-xs text-stone-500 mt-3 pt-3 border-t border-stone-200">
                  * Tasa de interés: 1.5% mensual
                </p>
              )}
            </div>

            <Button
              onClick={() => setShowFinancingModal(false)}
              className="w-full bg-[#064E3B] hover:bg-[#065f46] rounded-full"
              data-testid="confirm-financing-btn"
            >
              Aplicar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Comparison Modal */}
      <Dialog open={showComparisonModal} onOpenChange={setShowComparisonModal}>
        <DialogContent className="rounded-2xl max-w-2xl" data-testid="comparison-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Comparador de Planes</DialogTitle>
          </DialogHeader>
          <div className="pt-4">
            <Tabs defaultValue="financing" className="w-full">
              <TabsList className="grid w-full grid-cols-2 rounded-full bg-stone-100 p-1">
                <TabsTrigger value="financing" className="rounded-full data-[state=active]:bg-white">
                  Por Cuotas
                </TabsTrigger>
                <TabsTrigger value="discount" className="rounded-full data-[state=active]:bg-white">
                  Por Descuento
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="financing" className="mt-4">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Cuotas</th>
                      <th>Mensual USD</th>
                      <th>Mensual COP</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 3, 6, 12, 18, 24].map((months) => {
                      const interest = months > 1 ? 0.015 : 0;
                      const monthly = months > 1 
                        ? (totals.total * (1 + interest * months)) / months 
                        : totals.total;
                      const totalWithInterest = monthly * months;
                      return (
                        <tr key={months} className={financingMonths === months ? 'bg-[#064E3B]/5' : ''}>
                          <td className="font-semibold">{months} {months === 1 ? 'pago' : 'cuotas'}</td>
                          <td className="price-tag">${monthly.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                          <td className="price-tag">${(monthly * exchangeRate).toLocaleString('es-CO', { maximumFractionDigits: 0 })}</td>
                          <td className="price-tag text-stone-500">${totalWithInterest.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TabsContent>
              
              <TabsContent value="discount" className="mt-4">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th>Descuento</th>
                      <th>Ahorro USD</th>
                      <th>Total USD</th>
                      <th>Total COP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[0, 5, 10, 15, 20, 25, 30].map((discount) => {
                      const savings = totals.subtotal * (discount / 100);
                      const finalTotal = totals.subtotal - savings;
                      return (
                        <tr key={discount} className={discountPercent === discount ? 'bg-[#064E3B]/5' : ''}>
                          <td className="font-semibold">{discount}%</td>
                          <td className="price-tag text-[#C2410C]">
                            {discount > 0 ? `-$${savings.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '-'}
                          </td>
                          <td className="price-tag">${finalTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                          <td className="price-tag">${(finalTotal * exchangeRate).toLocaleString('es-CO', { maximumFractionDigits: 0 })}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Treatment Modal with Maxilar/Viaje Selection */}
      <Dialog open={showAddTreatmentModal} onOpenChange={setShowAddTreatmentModal}>
        <DialogContent className="rounded-2xl" data-testid="add-treatment-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {treatmentToAdd?.treatment.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-4">
            <div className="bg-stone-50 rounded-xl p-4">
              <p className="text-sm text-stone-600">{treatmentToAdd?.treatment.description}</p>
              <p className="text-lg font-bold text-[#064E3B] mt-2 price-tag">
                {treatmentToAdd && formatPrice(treatmentToAdd.treatment.price_usd)}
              </p>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-3 block">Maxilar</Label>
              <div className="grid grid-cols-3 gap-2">
                {MAXILAR_OPTIONS.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    onClick={() => setSelectedMaxilar(value)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      selectedMaxilar === value 
                        ? 'border-[#064E3B] bg-[#064E3B]/5' 
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    data-testid={`maxilar-${value}`}
                  >
                    {Icon ? (
                      <Icon className={`w-6 h-6 ${selectedMaxilar === value ? 'text-[#064E3B]' : 'text-stone-400'}`} />
                    ) : (
                      <CircleDot className={`w-6 h-6 ${selectedMaxilar === value ? 'text-[#064E3B]' : 'text-stone-400'}`} />
                    )}
                    <span className={`text-xs font-medium ${selectedMaxilar === value ? 'text-[#064E3B]' : 'text-stone-500'}`}>
                      {label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold mb-3 block">Número de Viaje</Label>
              <div className="grid grid-cols-4 gap-2">
                {VIAJE_OPTIONS.map((viaje) => (
                  <button
                    key={viaje}
                    onClick={() => setSelectedViaje(viaje)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      selectedViaje === viaje 
                        ? 'border-[#064E3B] bg-[#064E3B]/5' 
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    data-testid={`viaje-${viaje}`}
                  >
                    <Plane className={`w-5 h-5 ${selectedViaje === viaje ? 'text-[#064E3B]' : 'text-stone-400'}`} />
                    <span className={`text-sm font-bold ${selectedViaje === viaje ? 'text-[#064E3B]' : 'text-stone-500'}`}>
                      {viaje}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={addToBudget}
              className="w-full bg-[#064E3B] hover:bg-[#065f46] rounded-full h-12 font-semibold"
              data-testid="confirm-add-treatment-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Agregar al Presupuesto
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Configuration Modal */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="config-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuración
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            {/* Exchange Rate */}
            <div className="bg-stone-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="w-5 h-5 text-[#0070F3]" />
                <Label className="font-semibold">Tasa de Cambio (TRM)</Label>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-stone-500 text-sm">1 USD =</span>
                <Input
                  type="number"
                  value={tempExchangeRate}
                  onChange={(e) => setTempExchangeRate(parseFloat(e.target.value) || 0)}
                  className="flex-1 font-mono text-lg h-12 rounded-xl"
                  data-testid="exchange-rate-input"
                />
                <span className="text-stone-500 text-sm">COP</span>
              </div>
              <p className="text-xs text-stone-400 mt-2">
                Tasa actual: ${exchangeRate.toLocaleString('es-CO')} COP/USD
              </p>
            </div>

            {/* Edit Prices Button */}
            <div className="bg-stone-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-[#0070F3]" />
                  <div>
                    <Label className="font-semibold">Precios de Tratamientos</Label>
                    <p className="text-xs text-stone-400">Editar precios del catálogo</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowConfigModal(false);
                    setShowPricesModal(true);
                  }}
                  className="rounded-full"
                  data-testid="edit-prices-btn"
                >
                  Editar Precios
                </Button>
              </div>
            </div>

            <Button
              onClick={saveExchangeRate}
              className="w-full bg-[#0070F3] hover:bg-[#0060D3] rounded-full h-12 font-semibold"
              data-testid="save-config-btn"
            >
              <Save className="w-5 h-5 mr-2" />
              Guardar Configuración
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Prices Editor Modal */}
      <Dialog open={showPricesModal} onOpenChange={setShowPricesModal}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[80vh]" data-testid="prices-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
              <Edit3 className="w-5 h-5" />
              Editar Tratamientos
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6 pt-4">
              {categories.map((category) => (
                <div key={category.id} className="space-y-3">
                  <div className="flex items-center justify-between sticky top-0 bg-white py-2">
                    <h3 className="font-semibold text-stone-900 flex items-center gap-2">
                      <IconComponent iconName={category.icon} className="w-4 h-4 text-[#0070F3]" />
                      {category.name}
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAddTreatmentForm(showAddTreatmentForm === category.id ? null : category.id)}
                      className="rounded-full text-xs"
                      data-testid={`add-treatment-category-${category.id}`}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Agregar
                    </Button>
                  </div>

                  {/* Add Treatment Form */}
                  {showAddTreatmentForm === category.id && (
                    <div className="bg-[#0070F3]/5 border border-[#0070F3]/20 rounded-xl p-4 space-y-3">
                      <Input
                        placeholder="Nombre del tratamiento"
                        value={newTreatmentForm.name}
                        onChange={(e) => setNewTreatmentForm({ ...newTreatmentForm, name: e.target.value })}
                        className="rounded-lg"
                        data-testid="new-treatment-name"
                      />
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          placeholder="Precio USD"
                          value={newTreatmentForm.price}
                          onChange={(e) => setNewTreatmentForm({ ...newTreatmentForm, price: e.target.value })}
                          className="flex-1 rounded-lg font-mono"
                          data-testid="new-treatment-price"
                        />
                        <Button
                          onClick={() => addNewTreatment(category.id)}
                          className="bg-[#0070F3] hover:bg-[#0060D3] rounded-lg"
                          data-testid="confirm-new-treatment"
                        >
                          Agregar
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    {getCategoryTreatments(category).map((treatment) => {
                      const customPrice = editingPrices[treatment.id];
                      const currentPrice = customPrice !== undefined ? customPrice : treatment.price_usd;
                      const customName = customTreatmentNames[treatment.id];
                      const currentName = customName !== undefined ? customName : treatment.name;
                      const isEdited = (customPrice !== undefined && customPrice !== treatment.price_usd) || 
                                       (customName !== undefined && customName !== treatment.name);
                      
                      return (
                        <div 
                          key={treatment.id} 
                          className={`p-3 rounded-xl border ${isEdited || treatment.isCustom ? 'border-[#0070F3] bg-[#0070F3]/5' : 'border-stone-200'}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Input
                              value={currentName}
                              onChange={(e) => updateTreatmentName(treatment.id, e.target.value)}
                              className="flex-1 font-medium text-sm h-9 rounded-lg"
                              data-testid={`name-input-${treatment.id}`}
                            />
                            {treatment.isCustom && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteCustomTreatment(category.id, treatment.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-9 w-9 p-0"
                                data-testid={`delete-treatment-${treatment.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-stone-500 text-sm">$</span>
                            <Input
                              type="number"
                              value={currentPrice}
                              onChange={(e) => updateTreatmentPrice(treatment.id, e.target.value)}
                              className="w-28 font-mono text-right h-9 rounded-lg"
                              data-testid={`price-input-${treatment.id}`}
                            />
                            <span className="text-stone-500 text-sm">USD</span>
                            {!treatment.isCustom && isEdited && (
                              <span className="text-xs text-stone-400 ml-2">
                                (Original: ${treatment.price_usd})
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          <div className="pt-4 border-t border-stone-200">
            <Button
              onClick={() => setShowPricesModal(false)}
              className="w-full bg-[#0070F3] hover:bg-[#0060D3] rounded-full h-12 font-semibold"
              data-testid="close-prices-btn"
            >
              Guardar y Cerrar
            </Button>
            <p className="text-xs text-stone-400 text-center mt-2">
              Los cambios se guardan automáticamente
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;
